import { approvedAdChannelCount } from '../channels';
import type { Feature } from '../features';

export type ScenarioValue = {
  /** 0..1. Drives the bar width only. Never rendered as text. */
  fraction: number;
  /** What the analyst reads: '40%', '3 of 6'. The meaning never rests on the bar alone. */
  text: string;
};

export type ScenarioRow = {
  label: string;
  current: ScenarioValue;
  recommended: ScenarioValue;
};

export type ScenarioColumns = {
  /** Heads the label column: 'Channel', 'Metric', 'Optimization', 'Channel family'. */
  subject: string;
  current: string;
  recommended: string;
};

export type ScenarioId = 'rebalance' | 'attribution' | 'simulation' | 'validation' | 'channels';

export type PreviewScenario = {
  id: ScenarioId;
  eyebrow: string;
  title: string;
  subtitle: string;
  columns: ScenarioColumns;
  rows: readonly ScenarioRow[];
  footerHeadline: string;
  footerDetail: string;
};

/**
 * How many of `approvedAdChannelCount()` each channel family already connects.
 *
 * A partition of `APPROVED_CHANNEL_KEYS`, not an estimate — `scenarios.test.ts` asserts the
 * three sum to the derived count, so moving a channel between families or editing the
 * allow-list fails there rather than shipping a card whose rows contradict its own footer:
 *
 *   search       {provider.google.ads}, {provider.bing}
 *   social       {provider.facebook}, {provider.tiktokV1}, {provider.pinterest}
 *   nativeRetail {provider.criteo}, '{provider.amazon' (prefix)
 *
 * Amazon connects here but is absent from the public integrations page, so 2 is not visibly inside 13. Correct as is.
 */
export const CHANNEL_FAMILY_CONNECTED = { search: 2, social: 3, nativeRetail: 2 } as const;

/**
 * The size of each family in the full catalogue.
 *
 * Sizes read off the vendor's public integrations page: search 3 and social 6 against its "Paid
 * Ads" search entries and its separate "Social Media" heading, nativeRetail 13 the "Paid Ads"
 * remainder after search and paid social — so the three are NOT one partition, and nothing renders
 * their sum. The "Sample data" chip stays: the row VALUES are illustrative. Unexported on purpose.
 */
const CHANNEL_FAMILY_CATALOGUE = { search: 3, social: 6, nativeRetail: 13 } as const;

const countValue = (connected: number, catalogue: number): ScenarioValue => ({
  fraction: connected / catalogue,
  text: `${connected} of ${catalogue}`,
});

/**
 * Five preview scenarios, one row geometry.
 *
 * Every scenario reuses `ScenarioRow` — label, two bars, two value texts — so `ScenarioPreview`
 * is one renderer with one set of rendering tests. The channel scenario expresses counts
 * rather than percentages, which is why the row carries a `fraction` AND a `text` instead of
 * a single number: the bar is drawn from the fraction, the label is read from the text, and
 * `scenarios.test.ts` asserts the two agree so a bar can never contradict its own caption.
 *
 * Register is `copy.ts`'s: present tense, positive framing, no denial language, no
 * manufactured urgency, no vendor name (these strings must be honest on a white-label
 * tenant too, where a Nexoya-branded pitch in front of an agency's own client is the
 * failure). Every number is internally consistent — each footer detail is arithmetic on the
 * rows above it, and a test does that arithmetic. Sample data an analyst can disprove in ten
 * seconds costs more credibility than it earns.
 */
export const SCENARIOS: Readonly<Record<ScenarioId, PreviewScenario>> = {
  rebalance: {
    id: 'rebalance',
    eyebrow: 'Preview · Optimization proposal',
    title: 'Cross-channel budget reallocation',
    subtitle: 'Reallocate budget across your connected channels',
    // 'Proposed', not 'Recommended': the Optimize tab calls its output a proposal you apply.
    columns: { subject: 'Channel', current: 'Current', recommended: 'Proposed' },
    // 40 + 35 + 25 = 100, and 48 + 30 + 22 = 100. A three-way allocation that does not total
    // 100 is the tell that the card is decoration rather than a picture of the product.
    rows: [
      { label: 'Google Ads', current: { fraction: 0.4, text: '40%' }, recommended: { fraction: 0.48, text: '48%' } },
      { label: 'Meta Ads', current: { fraction: 0.35, text: '35%' }, recommended: { fraction: 0.3, text: '30%' } },
      { label: 'TikTok', current: { fraction: 0.25, text: '25%' }, recommended: { fraction: 0.22, text: '22%' } },
    ],
    footerHeadline: 'One budget application across 3 channels',
    // "from Meta Ads" alone would be false: Google's +8 is funded by Meta's −5 AND TikTok's
    // −3. Naming both is the difference between a mock and a claim.
    footerDetail: 'Shift 8% into Google Ads from Meta Ads and TikTok · applied to each channel for you',
  },

  attribution: {
    id: 'attribution',
    eyebrow: 'Preview · Attribution across the funnel',
    title: 'Where the conversions actually came from',
    subtitle: 'Modeled credit next to measured impact, across the funnel',
    columns: { subject: 'Channel', current: 'Measured', recommended: 'Attributed' },
    // 52 + 31 + 17 = 100, and 38 + 34 + 28 = 100: credit is redistributed, never created.
    rows: [
      { label: 'Google Ads', current: { fraction: 0.52, text: '52%' }, recommended: { fraction: 0.38, text: '38%' } },
      { label: 'Meta Ads', current: { fraction: 0.31, text: '31%' }, recommended: { fraction: 0.34, text: '34%' } },
      { label: 'TikTok', current: { fraction: 0.17, text: '17%' }, recommended: { fraction: 0.28, text: '28%' } },
    ],
    footerHeadline: 'Credit redistributed across 3 channels',
    // 28 − 17 = 11. The "no individual user signals" tail is not a flourish: it is the same
    // privacy claim `copy.ts` makes for `attribution.view`, and the two must not diverge.
    footerDetail:
      'TikTok carries 11% more of the funnel than measured impact shows · modeled, no individual user signals',
  },

  simulation: {
    id: 'simulation',
    eyebrow: 'Preview · Scenario simulation',
    title: 'A budget change, before you make it',
    subtitle: 'The projected outcome of one budget scenario',
    // The Simulations tab compares a base scenario against a scenario; it has no "today" column.
    columns: { subject: 'Metric', current: 'Base scenario', recommended: 'Scenario' },
    // Unit-free on purpose: currency is tenant-dependent, and a fabricated 'CHF 80k' is
    // exactly the kind of figure the fabricated-numbers tripwire in the dialog's test forbids.
    rows: [
      {
        label: 'Target achieved',
        current: { fraction: 0.74, text: '74%' },
        recommended: { fraction: 1, text: '100%' },
      },
      { label: 'Spent', current: { fraction: 0.86, text: '86%' }, recommended: { fraction: 1, text: '100%' } },
      {
        label: 'Cost-per',
        current: { fraction: 1, text: '100%' },
        recommended: { fraction: 0.86, text: '86%' },
      },
    ],
    footerHeadline: 'One scenario, simulated end to end',
    // (100/86) / (100/74) = 0.86, so 14 follows from the two rows above. The tail is the honest
    // half: a simulation that applied itself would be an optimization, sold as the wrong thing.
    footerDetail: 'Reaching 100% of target costs 14% less per outcome · nothing is applied until you say so',
  },

  validation: {
    id: 'validation',
    eyebrow: 'Preview · Predicted against actual',
    title: 'How close each optimization landed',
    subtitle: 'What was predicted, next to what actually happened',
    // 'Achieved' and the date-range labels are the Validation/Optimize tabs' own vocabulary
    // (`Validation.tsx:61`, `Optimize.tsx:318`) — optimizations are picked by period, never numbered.
    columns: { subject: 'Optimization', current: 'Predicted', recommended: 'Achieved' },
    // The middle one lands BELOW its prediction deliberately. Three that all beat forecast
    // would read as marketing; a miss is what makes the other two believable.
    rows: [
      {
        label: 'Mar 01 - Mar 14 2025',
        current: { fraction: 0.95, text: '95%' },
        recommended: { fraction: 0.98, text: '98%' },
      },
      {
        label: 'Mar 15 - Mar 28 2025',
        current: { fraction: 0.9, text: '90%' },
        recommended: { fraction: 0.87, text: '87%' },
      },
      {
        label: 'Mar 29 - Apr 11 2025',
        current: { fraction: 0.93, text: '93%' },
        recommended: { fraction: 0.96, text: '96%' },
      },
    ],
    footerHeadline: 'Three optimizations measured against what happened',
    // |98−95| = |87−90| = |96−93| = 3, so "within 3 points" is the exact bound, not a round one.
    footerDetail: 'Predictions landed within 3 points of achieved · measured per optimization, not claimed',
  },

  channels: {
    id: 'channels',
    eyebrow: 'Preview · The full channel catalog',
    title: 'Every channel you run, in one portfolio',
    subtitle: 'What your plan connects today, next to the full channel catalog',
    columns: { subject: 'Channel family', current: 'Your plan', recommended: 'Full platform' },
    // Counts, not percentages — this is the whole reason `ScenarioValue` carries a `text`
    // alongside its `fraction`. "3 of 6" is what a channel roster means; "50%" is not.
    rows: [
      {
        label: 'Paid search',
        current: countValue(CHANNEL_FAMILY_CONNECTED.search, CHANNEL_FAMILY_CATALOGUE.search),
        recommended: countValue(CHANNEL_FAMILY_CATALOGUE.search, CHANNEL_FAMILY_CATALOGUE.search),
      },
      {
        label: 'Paid social',
        current: countValue(CHANNEL_FAMILY_CONNECTED.social, CHANNEL_FAMILY_CATALOGUE.social),
        recommended: countValue(CHANNEL_FAMILY_CATALOGUE.social, CHANNEL_FAMILY_CATALOGUE.social),
      },
      {
        label: 'Native, retail & programmatic',
        current: countValue(CHANNEL_FAMILY_CONNECTED.nativeRetail, CHANNEL_FAMILY_CATALOGUE.nativeRetail),
        recommended: countValue(CHANNEL_FAMILY_CATALOGUE.nativeRetail, CHANNEL_FAMILY_CATALOGUE.nativeRetail),
      },
    ],
    // Interpolated, never a literal: a hardcoded "seven" drifts the day someone edits
    // `APPROVED_CHANNEL_KEYS`, and a wrong roster size is the one number in this card the
    // reader can check against their own integrations page.
    footerHeadline: `${approvedAdChannelCount()} channels today, the whole channel catalog on the full platform`,
    footerDetail:
      'The same portfolios and the same metrics, with the rest of your spend in them · nothing to re-import',
  },
};

/**
 * Which scenario a locked `Feature` lands on.
 *
 * A `Map`, not an object literal, for the same reason as `PLAN_FROM_WIRE` in `plans.ts` and
 * `FEATURE_BY_TAB` in `portfolioTabFeatures.ts`: callers reach here with values derived from
 * user input (`featureForPortfolioTab` off `?activeTab=`), and `literal['toString']` resolves
 * to the *function* `Object.prototype.toString` — truthy, so a `?? fallback` never fires and
 * a function escapes as a scenario. That exact bug has shipped twice in this directory's
 * neighbours. A Map has no inherited keys to index into at all.
 */
const SCENARIO_BY_FEATURE: ReadonlyMap<Feature, ScenarioId> = new Map<Feature, ScenarioId>([
  ['optimization.view', 'rebalance'],
  ['optimization.settings', 'rebalance'],
  ['optimization.contentControls', 'rebalance'],
  ['budget.reallocation.view', 'rebalance'],
  ['attribution.view', 'attribution'],
  ['attribution.configure', 'attribution'],
  ['simulation.view', 'simulation'],
  ['validation.view', 'validation'],
  ['channel.unrestricted', 'channels'],
  // Hidden rather than sold: both redirect at the route (`Routes.tsx`) and neither has a
  // teaser or a `LockedAction`, so this dialog never opens for them. The mapping exists to
  // satisfy the tripwire; give either one a teaser and it needs a card of its own first.
  ['metrics.view', 'rebalance'],
]);

/**
 * The card for `feature`, or the rebalance card when nothing maps.
 *
 * Falls back rather than returning null: a `Feature` added later still opens a coherent
 * dialog instead of a hole where the preview should be, and rebalancing is the capability
 * every other one in this tier is downstream of. The fallback is also why
 * `scenarios.test.ts` pins the mapped set — a new gated capability would otherwise inherit
 * a rebalance pitch silently, and silence is the only failure mode here worth guarding.
 */
export function scenarioFor(feature: Feature): PreviewScenario {
  return SCENARIOS[SCENARIO_BY_FEATURE.get(feature) ?? 'rebalance'];
}

/**
 * A bar's CSS width from a 0..1 fraction.
 *
 * Clamped and NaN-safe because both failures are silent and both point the wrong way:
 * `width: NaN%` is an invalid declaration the browser drops, leaving the fill at its
 * intrinsic width — a FULL bar — and an unclamped fraction above 1 paints past the end of
 * its own track. One decimal place, because a bar that is 66.66666666666666% wide serialises
 * that whole string into the DOM for no visible gain.
 */
export function barWidth(fraction: number): string {
  // Only NaN needs the guard: it is the one input `Math.min`/`Math.max` cannot resolve, and
  // it propagates through both. The infinities clamp like any other out-of-range number.
  if (Number.isNaN(fraction)) return '0%';

  const clamped = Math.min(1, Math.max(0, fraction));
  return `${Number((clamped * 100).toFixed(1))}%`;
}
